import { type ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { capitalize } from "../common/utils/string-utils/capitlize";
import {
	type LocaleGetMonth,
	LocaleContext,
	type FormatRange,
	type FormatRangeParts,
	type Format,
} from "./useLocale";
import {
	type Duration,
	formatDuration as durationFormat,
} from "src/common/utils/date-utils/getDuration";

const DIVISIONS: { amount: number; name: Intl.RelativeTimeFormatUnit }[] = [
	{ amount: 60, name: "seconds" },
	{ amount: 60, name: "minutes" },
	{ amount: 24, name: "hours" },
	{ amount: 7, name: "days" },
	{ amount: 4.34524, name: "weeks" },
	{ amount: 12, name: "months" },
	{ amount: Number.POSITIVE_INFINITY, name: "years" },
];

function largestDivision(seconds: number) {
	let duration = seconds;
	for (const division of DIVISIONS) {
		if (Math.abs(duration) < division.amount) {
			return { value: Math.round(duration), unit: division.name };
		}
		duration /= division.amount;
	}
	throw new Error("No division found");
}

function formatTimeAgo(formatter: Intl.RelativeTimeFormat, date: Date) {
	const { value, unit } = largestDivision((date.getTime() - Date.now()) / 1000);
	return formatter.format(value, unit);
}

const SINGULAR_UNIT: Record<Intl.RelativeTimeFormatUnit, string> = {
	second: "second",
	seconds: "second",
	minute: "minute",
	minutes: "minute",
	hour: "hour",
	hours: "hour",
	day: "day",
	days: "day",
	week: "week",
	weeks: "week",
	month: "month",
	months: "month",
	quarter: "quarter",
	quarters: "quarter",
	year: "year",
	years: "year",
};

export function formatElapsedSince(language: string, since: Date) {
	const { value, unit } = largestDivision(
		(Date.now() - since.getTime()) / 1000,
	);
	return new Intl.NumberFormat(language, {
		style: "unit",
		unit: SINGULAR_UNIT[unit],
		unitDisplay: "long",
	}).format(Math.max(value, 0));
}

const formatRangeCache: Record<string, Intl.DateTimeFormat> = {};
const formatCache: Record<string, Intl.DateTimeFormat> = {};

const browserHourCycle = new Intl.DateTimeFormat(undefined, {
	hour: "numeric",
}).resolvedOptions().hourCycle;

interface ProvideLocaleProps {
	children?: ReactNode | undefined;
}

export const ProvideLocale = ({ children }: ProvideLocaleProps) => {
	const { i18n } = useTranslation();

	const longMonthFormatter = useMemo(() => {
		return new Intl.DateTimeFormat(i18n.language, { month: "long" });
	}, [i18n.language]);
	const shortMonthFormatter = useMemo(() => {
		return new Intl.DateTimeFormat(i18n.language, { month: "short" });
	}, [i18n.language]);

	const getMonthsInYear = useCallback(() => {
		return [...Array(12).keys()].map((m) =>
			capitalize(longMonthFormatter.format(new Date(1987, m))),
		);
	}, [longMonthFormatter]);

	const getMonth = useCallback<LocaleGetMonth>(
		(monthOrDate, type = "long") => {
			const formatter =
				type === "long" ? longMonthFormatter : shortMonthFormatter;
			return capitalize(
				formatter.format(
					typeof monthOrDate === "number"
						? new Date(1987, monthOrDate)
						: monthOrDate,
				),
			);
		},
		[longMonthFormatter, shortMonthFormatter],
	);

	const formatRange = useCallback<FormatRange>(
		(start, end) => {
			let formatter: Intl.DateTimeFormat;
			const cached = formatRangeCache[i18n.language];
			if (cached) {
				formatter = cached;
			} else {
				formatter = new Intl.DateTimeFormat(i18n.language, {
					year: "numeric",
					month: "short",
				});
				formatRangeCache[i18n.language] = formatter;
			}

			return formatter.formatRange(start, end);
		},
		[i18n.language],
	);

	const formatRangeParts = useCallback<FormatRangeParts>(
		(start, end) => {
			return new Intl.DateTimeFormat(i18n.language, {
				year: "numeric",
				month: "long",
			}).formatRangeToParts(start, end);
		},
		[i18n.language],
	);

	const format = useCallback<Format>(
		(date, options) => {
			try {
				const timeOptions: Intl.DateTimeFormatOptions | undefined = options
					? { hourCycle: browserHourCycle, ...options }
					: options;
				const cacheKey = `${i18n.language}-${JSON.stringify(timeOptions)}`;
				const cached = formatCache[cacheKey];
				let formatter: Intl.DateTimeFormat;
				if (cached) {
					formatter = cached;
				} else {
					formatter = new Intl.DateTimeFormat(i18n.language, timeOptions);
					formatCache[cacheKey] = formatter;
				}

				return formatter.format(date);
			} catch (e) {
				console.error(`Failed to format time value: ${date}`);
				throw e;
			}
		},
		[i18n.language],
	);

	const relativeFormatter = useMemo(() => {
		return new Intl.RelativeTimeFormat(i18n.language);
	}, [i18n.language]);

	const formatRelative = useCallback(
		(date: Date) => {
			return formatTimeAgo(relativeFormatter, date);
		},
		[relativeFormatter],
	);

	const formatElapsed = useCallback(
		(since: Date) => formatElapsedSince(i18n.language, since),
		[i18n.language],
	);

	const numberFormatter = useMemo(() => {
		return new Intl.NumberFormat(i18n.language);
	}, [i18n.language]);

	const formatNumber = useCallback(
		(number: number | bigint) => {
			return numberFormatter.format(number);
		},
		[numberFormatter],
	);

	const durationFormatter = useMemo(() => {
		if (!("DurationFormat" in Intl)) {
			return {
				format: (input: Duration) => {
					return durationFormat(input, { shortUnits: true });
				},
			};
		}

		const formatter = new (Intl as any).DurationFormat(i18n.language, {
			style: "narrow",
		});
		return formatter;
	}, [i18n.language]);

	const formatDuration = useCallback(
		(duration: Duration) => {
			return durationFormatter.format(duration);
		},
		[durationFormatter],
	);

	return (
		<LocaleContext.Provider
			value={{
				getMonthsInYear,
				getMonth,
				formatRange,
				format,
				formatRangeParts,
				formatRelative,
				formatElapsed,
				lang: i18n.language,
				formatNumber,
				formatDuration,
			}}
		>
			{children}
		</LocaleContext.Provider>
	);
};
